import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { IDestinatarioAsistencia, IEmergencia, IQueryParam } from 'modelos/src';
import { retry, Subscription, throwError, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { IColumnas } from '../../../../../../auxiliares/tabla/tabla.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { PIEZAS_DETALLE } from '../../../../../../componentes/detalle/detalle.components';
import {
  estadoCeldaEmergencia,
  estadoDe,
  prioridadCelda,
} from '../../../estado-emergencia';
import { fechaCelda } from '../../../../../../auxiliares/tabla/celdas';
import {
  htmlDireccion,
  labelSexo,
  nombrePaciente,
  sinDato,
  tooltipDireccion,
} from '../../presentacion-entidades';
import { CrearEditarDestinatarioAsistenciaComponent } from '../crear-editar-destinatario-asistencia/crear-editar-destinatario-asistencia.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-detalles-destinatario-asistencia',
  imports: [AuxiliaresModule, ...PIEZAS_DETALLE],
  templateUrl: './detalles-destinatario-asistencia.component.html',
  styleUrls: [
    '../../../../../../componentes/detalle/detalle-pagina.scss',
    '../../detalle-emergencias.scss',
  ],
})
export class DetallesDestinatarioAsistenciaComponent
  implements OnInit, OnDestroy
{
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  helper = inject(HelperService);

  public loading = true;
  public id = '';
  public paciente?: IDestinatarioAsistencia;
  private paciente$?: Subscription;
  public selectedTab = 0;

  public nombre = '';
  /**
   * El cliente, sin HTML. Se calcula al dibujar: el índice de clientes de la
   * jerarquía puede llegar después que la entidad (al recargar la página el
   * chip quedaba vacío).
   */
  get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    const e = this.paciente;
    const cadena = this.clienteJerarquia.cadena(e?.cliente ?? e?.idCliente);
    return cadena[cadena.length - 1]?.nombre || e?.cliente?.nombre || undefined;
  }
  public fechaAlta?: string;
  public edad?: string;
  public sexo?: string | null;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  public puedeEditar = this.helper.tienePermiso(
    'Emergencias Médicas - Editar pacientes',
  );

  // Solapa Emergencias
  public nameEmergencias = 'EmergenciasPacienteComponent';
  public emergencias?: IEmergencia[];
  public totalEmergencias = 0;
  public columnasEmergencias: IColumnas<IEmergencia>[] = [];
  private emergencias$?: Subscription;
  public queryEmergencias: IQueryParam = {};

  private listar(): void {
    this.paciente$?.unsubscribe();
    this.paciente$ = this.lista2
      .destinatarioAsistencia(this.id, 30)
      // Un request que falla terminaba el refresco de cada 30 s para siempre.
      // Si todavía no se cargó nunca, el error sigue de largo.
      .pipe(
        retry({
          delay: (error) =>
            this.paciente ? timer(30000) : throwError(() => error),
        }),
      )
      .subscribe({
        next: (data) => {
          if (!HelperService.cambiaronLosDatos(this.paciente, data)) return;
          this.paciente = data;
          this.armar();
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this.helper.notifError(error);
          // No existe o no hay acceso: sin esto quedaba el spinner para siempre
          this.loading = false;
        },
      });
  }

  private armar(): void {
    const p = this.paciente;
    if (!p) return;
    this.nombre = nombrePaciente(p) || 'Paciente';
    this.edad =
      p.edad !== undefined && p.edad !== null
        ? `${p.edad} ${p.edad === 1 ? 'año' : 'años'}`
        : undefined;
    this.sexo = labelSexo(p.sexo);
    this.fechaAlta = p.fechaCreacion
      ? HelperService.getFechaYHora(p.fechaCreacion)
      : undefined;
  }

  // Solapa Emergencias

  public listarEmergencias(): void {
    // El filtro por paciente se agrega acá: la tabla pisa `query.filter`.
    const query: IQueryParam = {
      ...this.queryEmergencias,
      filter: JSON.stringify({ idDestinatarioAsistencia: this.id }),
    };
    this.emergencias$?.unsubscribe();
    this.emergencias$ = this.lista2.emergencias(query, 30).subscribe((data) => {
      if (HelperService.cambiaronLosDatos(this.emergencias, data.datos)) {
        this.emergencias = data.datos;
      }
      this.totalEmergencias = data.totalCount;
    });
  }

  private setColumnasEmergencias(): void {
    this.columnasEmergencias = [
      {
        header: { label: 'Emergencia', sort: 'fechaCreacion', sortable: true },
        row: {
          html: (e) => {
            const codigo =
              e.codigo !== undefined
                ? `<span class="tabla-principal">#${e.codigo}</span>`
                : sinDato('Sin código');
            const fecha = e.fechaCreacion
              ? `<span class="tabla-secundario">${fechaCelda(e.fechaCreacion)}</span>`
              : '';
            return `<div class="tabla-celda-col">${codigo}${fecha}</div>`;
          },
        },
      },
      {
        header: { label: 'Estado' },
        row: { estado: (e) => estadoCeldaEmergencia(estadoDe(e)) },
      },
      {
        header: {
          label: 'Código de apertura',
          sort: 'prioridadApertura',
          sortable: true,
        },
        row: {
          estado: (e) =>
            e.prioridadApertura
              ? prioridadCelda(e.prioridadApertura)
              : undefined,
          noData: 'Sin código',
        },
      },
      {
        header: { label: 'Dirección' },
        row: {
          html: (e) => htmlDireccion(e.direccion),
          tooltip: (e) => tooltipDireccion(e.direccion),
        },
      },
      {
        header: { label: 'Acciones' },
        row: {
          accionesGrouped: [
            {
              tipo: 'material',
              icon: 'visibility',
              tooltip: 'Ver detalle',
              click: (e) =>
                this.router.navigate([
                  'main/emergencias/emergenciasMedicas/detalles',
                  e._id,
                ]),
            },
          ],
        },
      },
    ];
  }

  // Navegación

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'emergencias'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string | null): number {
    if (!tab) return 0;
    if (/^\d+$/.test(tab)) return parseInt(tab);
    return Math.max(0, this.pestanas().indexOf(tab));
  }

  /** Edita en diálogo; al guardar se recarga el detalle. */
  public editar(): void {
    const ref = this.formularios.abrir(
      CrearEditarDestinatarioAsistenciaComponent,
      { tamano: 'mediano', data: { idDestinatario: this.id } },
    );
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  ngOnInit(): void {
    // El tab de la URL, también cuando cambia sin salir del detalle
    this.queryParams$ = this.route.queryParamMap.subscribe((qp) => {
      this.selectedTab = this.indicePestana(qp.get('tab'));
    });

    this.queryEmergencias = {
      sort: '-fechaCreacion',
      page: 0,
      limit: +this.helper.getPageSize(this.nameEmergencias),
      includeChildren: true,
    };
    this.setColumnasEmergencias();

    // Al cambiar el id de la ruta Angular reutiliza el componente
    this.params$ = this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
      this.loading = true;
      this.paciente = undefined;
      this.listar();
      this.listarEmergencias();
    });
  }

  ngOnDestroy(): void {
    this.paciente$?.unsubscribe();
    this.emergencias$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
